// „Limit setzen“ neben der Trefferzahl (Agent 10B, Runde 10, Close: „5,312 Leads (Set limit)“): begrenzt die Liste auf die ersten N Treffer
// in der Reihenfolge der Liste. Das Limit ist `results_limit` der Abfrage; die Seite macht daraus einen Filter-Entwurf, „Speichern“ im
// Listenkopf legt es in der Smart View ab, und der Power Dialer ruft dann nur diese N Leads an.
import { useEffect, useRef, useState } from "react";
import { Ausklapp } from "../Ausklapp";
import { LIMIT_MAX, LIMIT_VORSCHLAEGE, limitKnopfText, limitLesen, limitVonText } from "./berechnungModell";
import "./berechnung.css";

interface Props {
  /** gesetztes Limit der Liste (null = keins) */
  limit: number | null;
  /** Treffer ohne das Limit, wenn bekannt */
  gesamt: number | null;
  /** Reihenfolge, nach der die ersten N gewählt werden, als Text („Zuletzt geändert, absteigend“) */
  reihenfolge: string;
  /** neues Limit (null = entfernen) */
  onSetzen: (limit: number | null) => void;
  /** false: die Abfrage lässt sich nicht ändern (z. B. geladen, Smart View ohne Abfrage) */
  moeglich?: boolean;
}

export function LimitSteuerung({ limit, gesamt, reihenfolge, onSetzen, moeglich = true }: Props) {
  const [offen, setOffen] = useState(false);
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [text, setText] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);

  // nur beim Öffnen vorbelegen: ändert sich das Limit von außen (Liste lädt neu), darf eine laufende Eingabe nicht überschrieben werden
  const limitRef = useRef(limit);
  limitRef.current = limit;
  useEffect(() => {
    if (!offen) return;
    setText(limitRef.current === null ? "" : String(limitRef.current));
    setMeldung(null);
    const t = window.setTimeout(() => {
      eingabe.current?.focus();
      eingabe.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [offen]);

  if (!moeglich) return null;

  const uebernehmen = (roh: string) => {
    const p = limitLesen(roh);
    if (!p.ok) {
      setMeldung(p.meldung);
      return;
    }
    setOffen(false);
    if (p.limit !== limit) onSetzen(p.limit);
  };
  const von = limitVonText(limit, gesamt);

  return (
    <span className="limit">
      <button
        ref={setAnker}
        type="button"
        className={`limit__knopf${limit !== null ? " limit__knopf--an" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={offen}
        title={limit === null ? "Die Liste auf die ersten Treffer begrenzen, zum Beispiel für den Power Dialer" : "Limit ändern oder entfernen"}
        onClick={() => setOffen((o) => !o)}
      >
        {limitKnopfText(limit)}
      </button>
      {von ? <span className="limit__von">{von}</span> : null}
      <Ausklapp anker={anker} offen={offen} onSchliessen={() => setOffen(false)} breite={320} rolle="dialog">
        <form
          className="limit__pop"
          role="dialog"
          aria-label="Limit setzen"
          onSubmit={(e) => {
            e.preventDefault();
            uebernehmen(text);
          }}
        >
          <div className="limit__titel">Treffer begrenzen</div>
          <p className="limit__text">
            Nur die ersten Treffer zählen, in der Reihenfolge: <b>{reihenfolge}</b>. Das gilt für die Liste, die Berechnungszeile und den Power
            Dialer der gespeicherten Smart View.
          </p>
          <label className="limit__feld">
            <span>Höchstzahl der Treffer</span>
            <input
              ref={eingabe}
              className={`eingabe${meldung ? " eingabe--fehler" : ""}`}
              inputMode="numeric"
              autoComplete="off"
              placeholder="zum Beispiel 100"
              maxLength={String(LIMIT_MAX).length + 2}
              value={text}
              aria-invalid={!!meldung}
              aria-describedby={meldung ? "limit-meldung" : undefined}
              onChange={(e) => {
                setText(e.target.value);
                setMeldung(null);
              }}
            />
          </label>
          {meldung ? (
            <div id="limit-meldung" className="limit__fehler" role="alert">
              {meldung}
            </div>
          ) : null}
          <div className="limit__vorschlaege" role="group" aria-label="Vorschläge">
            {LIMIT_VORSCHLAEGE.map((n) => (
              <button key={n} type="button" className="limit__chip" aria-pressed={text.trim() === String(n)} onClick={() => uebernehmen(String(n))}>
                {n.toLocaleString("de-DE")}
              </button>
            ))}
          </div>
          <div className="limit__fuss">
            {limit !== null ? (
              <button type="button" className="textknopf" onClick={() => (setOffen(false), onSetzen(null))}>
                Limit entfernen
              </button>
            ) : (
              <span />
            )}
            <button type="submit" className="knopf knopf--primaer knopf--klein">
              Übernehmen
            </button>
          </div>
        </form>
      </Ausklapp>
    </span>
  );
}
